import React from 'react';
import { useTranslation } from "react-i18next";
import { format, parseISO } from 'date-fns';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { CalendarPlus, Plus, FileX2, Settings2, PencilLine } from 'lucide-react';
import { fmtHours, fmtMinutes, getDateFnsLocale } from '../../utils/attendanceUtils';
import StatusBadge from './StatusBadge';

const AttendanceDetailTable = ({
  paginatedResults,
  absenceReasons,
  openAssignDialog,
  openManualPunchDialog,
  openAbsenceDialog,
  openMenuId,
  setOpenMenuId,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = getDateFnsLocale(i18n.language);
  return (
    <table className="w-full caption-bottom text-sm">
      <TableHeader className="sticky top-0 z-10 shadow-sm">
        <TableRow className="bg-muted/50 shadow-sm">
          <TableHead className="w-28">{t("attComp.detailTable.colDate")}</TableHead>
          <TableHead className="w-28">{t("attComp.matricule")}</TableHead>
          <TableHead>{t("attComp.lastName")}</TableHead>
          <TableHead>{t("attComp.firstName")}</TableHead>
          <TableHead className="whitespace-nowrap">{t("attComp.detailTable.colSchedule")}</TableHead>
          <TableHead className="text-center whitespace-nowrap">{t("attComp.detailTable.colStart")}</TableHead>
          <TableHead className="text-center whitespace-nowrap">{t("attComp.detailTable.colEnd")}</TableHead>
          <TableHead className="text-center whitespace-nowrap">{t("attComp.detailTable.realEntry")}</TableHead>
          <TableHead className="text-center whitespace-nowrap">{t("attComp.detailTable.realExit")}</TableHead>
          <TableHead className="text-center whitespace-nowrap">{t("attComp.detailTable.colLate")}</TableHead>
          <TableHead className="text-center whitespace-nowrap">{t("attComp.detailTable.earlyDep")}</TableHead>
          <TableHead className="text-center whitespace-nowrap">{t("attComp.detailTable.colWorked")}</TableHead>
          <TableHead className="text-center whitespace-nowrap">{t("attComp.detailTable.colOvertime")}</TableHead>
          <TableHead className="text-center">{t("attComp.detailTable.colStatus")}</TableHead>
          <TableHead className="w-8"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {paginatedResults.length === 0 ? (
          <TableRow>
            <TableCell colSpan={15} className="text-center py-12 text-muted-foreground">{t("attComp.noResult")}</TableCell>
          </TableRow>
        ) : (
          paginatedResults.map((r, idx) => {
            const rowKey = `${r.employee_id}-${r.date}`;
            const prevR = idx > 0 ? paginatedResults[idx - 1] : null;
            const isFirstOfEmployee = !prevR || prevR.employee_id !== r.employee_id;

            const absenceReason = r.absence_reason_id
              ? absenceReasons.find(ar => ar.id === r.absence_reason_id)
              : null;
            const isActuallyAbsent = r.status === 'absent' || r.status === 'absence_justifiee';
            const workedHoursRaw = r.worked_hours;
            const expectedHoursRaw = r.expected_hours;
            const showBar = !isActuallyAbsent && r.status !== 'public_holiday'
              && r.status !== 'absent' && r.status !== 'no_schedule'
              && r.status !== 'day_off'
              && workedHoursRaw != null && expectedHoursRaw > 0;
            const ratio = showBar ? Math.min(1, workedHoursRaw / expectedHoursRaw) : 0;
            const barColor = ratio >= 0.9 ? 'bg-green-500' : ratio >= 0.7 ? 'bg-yellow-400' : 'bg-red-400';

            const workedHoursCell = (absenceReason && isActuallyAbsent)
              ? (absenceReason.is_paid
                  ? <span className="font-mono text-sm font-semibold text-foreground">{r.expected_hours != null ? fmtHours(r.expected_hours) : '—'}</span>
                  : <span className="text-muted-foreground text-sm">0h</span>)
              : r.status === 'public_holiday'
              ? <span className="font-mono text-sm font-semibold text-purple-400">{r.expected_hours != null ? fmtHours(r.expected_hours) : '—'}</span>
              : (workedHoursRaw != null
                  ? <div className="flex flex-col items-center gap-1">
                      <span className="font-mono text-sm font-semibold">{fmtHours(workedHoursRaw)}</span>
                      {showBar && (
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${ratio * 100}%` }} />
                        </div>
                      )}
                    </div>
                  : <span className="text-muted-foreground">—</span>);

            return (
              <React.Fragment key={`${rowKey}-${idx}`}>
                {isFirstOfEmployee && (
                  <tr className={`${idx > 0 ? 'border-t-2 border-border' : ''} bg-muted/40`}>
                    <td colSpan={15} className="px-3 py-1.5 text-xs font-semibold text-foreground">
                      {r.last_name} {r.first_name}
                      <span className="ml-2 font-normal text-muted-foreground font-mono">{r.registration_number}</span>
                    </td>
                  </tr>
                )}
                <TableRow
                  className={
                    r.status === 'public_holiday'  ? 'bg-purple-950/20' :
                    r.status === 'absent'          ? 'bg-red-950/20' :
                    r.status === 'late'            ? 'bg-orange-950/20' :
                    r.status === 'early_departure' ? 'bg-yellow-950/20' :
                    r.status === 'missing_exit'    ? 'bg-muted/40' : ''
                  }>
                  <TableCell className="text-sm whitespace-nowrap">
                    {format(parseISO(r.date + 'T00:00:00'), 'EEE dd/MM', { locale: dateLocale })}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.registration_number}</TableCell>
                  <TableCell className="font-medium">{r.last_name}</TableCell>
                  <TableCell>{r.first_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {r.schedule_name || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-center font-mono text-sm text-muted-foreground">
                    {r.expected_start || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-center font-mono text-sm text-muted-foreground">
                    {r.expected_end || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {r.status === 'public_holiday'
                      ? <span className="text-xs font-medium text-purple-400 bg-purple-950/20 px-2 py-0.5 rounded-full">{r.holiday_name || t('attComp.holidayFallback')}</span>
                      : r.actual_entry ? (
                        r.actual_entry_is_manual
                          ? <span className="font-mono text-sm font-semibold text-primary italic inline-flex items-center gap-0.5">
                              <PencilLine className="h-3 w-3" />{r.actual_entry}
                            </span>
                          : <span className="font-mono text-sm font-semibold">{r.actual_entry}</span>
                      ) : <span className="text-muted-foreground text-sm">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {r.status === 'public_holiday'
                      ? null
                      : r.actual_exit ? (
                        r.actual_exit_is_manual
                          ? <span className="font-mono text-sm font-semibold text-primary italic inline-flex items-center gap-0.5">
                              <PencilLine className="h-3 w-3" />{r.actual_exit}
                            </span>
                          : <span className="font-mono text-sm font-semibold">{r.actual_exit}</span>
                      ) : <span className="text-muted-foreground text-sm">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {r.lateness_minutes > 0
                      ? <span className="text-red-600 font-semibold text-sm">+{fmtMinutes(r.lateness_minutes)}</span>
                      : <span className="text-green-600 text-sm">0</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {r.early_departure_minutes > 0
                      ? <span className="text-orange-600 font-semibold text-sm">-{fmtMinutes(r.early_departure_minutes)}</span>
                      : <span className="text-green-600 text-sm">0</span>}
                  </TableCell>
                  <TableCell className="text-center">{workedHoursCell}</TableCell>
                  <TableCell className="text-center">
                    {r.overtime_hours > 0
                      ? <span className="text-purple-600 font-semibold text-sm">+{fmtHours(r.overtime_hours)}</span>
                      : <span className="text-gray-300">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {r.status === 'no_schedule' ? (
                      <button onClick={() => openAssignDialog(r)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground hover:bg-accent hover:text-foreground border border-dashed border-border transition-colors">
                        <CalendarPlus className="h-3 w-3" /> {t("attComp.detailTable.assignSchedule")}
                      </button>
                    ) : (r.status === 'absent' || r.status === 'missing_exit' || r.status === 'absence_justifiee') ? (
                      <button onClick={() => openAbsenceDialog(r)}
                        className="text-left hover:opacity-75 transition-opacity inline-flex items-center gap-1 flex-wrap">
                        <StatusBadge status={r.status} />
                        {r.absence_reason_name && (
                          <span className="text-xs font-medium" style={{ color: r.absence_reason_color }}>· {r.absence_reason_name}</span>
                        )}
                      </button>
                    ) : (
                      <StatusBadge status={r.status} />
                    )}
                  </TableCell>
                  <TableCell className="w-8 px-1">
                    <Popover
                      open={openMenuId === rowKey}
                      onOpenChange={open => setOpenMenuId(open ? rowKey : null)}
                    >
                      <PopoverTrigger asChild>
                        <button title={t("attComp.detailTable.actions")} className="p-1 rounded hover:bg-muted text-gray-400 hover:text-foreground transition-colors">
                          <Settings2 className="h-3.5 w-3.5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-52 p-1" align="end">
                        <button
                          onClick={() => { setOpenMenuId(null); openManualPunchDialog(r); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted text-left"
                        >
                          <Plus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          {t("attComp.detailTable.addPunch")}
                        </button>
                        <button
                          onClick={() => { setOpenMenuId(null); openAbsenceDialog(r); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted text-left"
                        >
                          <FileX2 className="h-3.5 w-3.5 text-red-500 shrink-0" />
                          {t("attComp.detailTable.assignAbsence")}
                        </button>
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })
        )}
      </TableBody>
    </table>
  );
};

export default AttendanceDetailTable;
